import { ReactNode, useState } from "react";
import { Check, ChevronDown, Loader2, Users } from "lucide-react";
import { PROFILE_SEARCH_MIN_LENGTH, useProfileSearch } from "@/hooks/auth/useProfiles";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Profile } from "@/types/auth/auth";

export const profileLabel = (profile: Profile) => profile.full_name || profile.email || profile.id;

const ProfileInitial = ({ profile }: { profile: Profile | null }) => (
  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#4E9CFF] to-[#FF4DB8] text-xs font-bold text-white">
    {profile ? profileLabel(profile).charAt(0).toUpperCase() : <Users className="h-4 w-4" />}
  </span>
);

interface UserSearchPickerProps {
  value: string | null;
  onChange: (profile: Profile | null) => void;
  allLabel?: string;
  /** The button that opens the picker. */
  children: ReactNode;
}

/** Server-side user search by name or email, opened from any trigger. */
export const UserSearchPicker = ({ value, onChange, allLabel = "All Users", children }: UserSearchPickerProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { data: profiles = [], enabled, isFetching, isDebouncing } = useProfileSearch(search);

  const tooShort = search.trim().length < PROFILE_SEARCH_MIN_LENGTH;
  const searching = !tooShort && (isDebouncing || isFetching);
  const results = enabled && !tooShort ? profiles : [];

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  const pick = (profile: Profile | null) => {
    onChange(profile);
    changeOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[min(24rem,calc(100vw-2rem))] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search by name or email..."
            className="text-base sm:text-sm"
          />
          <CommandList className="max-h-80">
            <CommandGroup>
              <CommandItem value="__all-users__" onSelect={() => pick(null)} className="flex items-center gap-3 cursor-pointer">
                <ProfileInitial profile={null} />
                <span className="flex-1 text-sm font-semibold text-gray-900">{allLabel}</span>
                {!value && <Check className="h-4 w-4 text-genie-primary" />}
              </CommandItem>
            </CommandGroup>

            {tooShort ? (
              <p className="px-3 py-5 text-center text-sm text-gray-500">
                Type at least {PROFILE_SEARCH_MIN_LENGTH} characters to find a user
              </p>
            ) : results.length > 0 ? (
              <CommandGroup heading={searching ? "Searching..." : "Users"}>
                {results.map((profile) => (
                  <CommandItem
                    key={profile.id}
                    value={profile.id}
                    onSelect={() => pick(profile)}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <ProfileInitial profile={profile} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-gray-900">{profileLabel(profile)}</div>
                      {profile.full_name && profile.email && (
                        <div className="truncate text-xs text-gray-500">{profile.email}</div>
                      )}
                    </div>
                    {value === profile.id && <Check className="h-4 w-4 text-genie-primary" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : searching ? (
              <p className="flex items-center justify-center gap-2 px-3 py-5 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin text-genie-primary" />
                Searching users...
              </p>
            ) : (
              <p className="px-3 py-5 text-center text-sm text-gray-500">No users match "{search.trim()}"</p>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface UserFilterProps {
  selectedUserId: string | null;
  onUserChange: (userId: string | null) => void;
  placeholder?: string;
  showLabel?: boolean;
}

export const UserFilter = ({
  selectedUserId,
  onUserChange,
  placeholder = "All Users",
  showLabel = true,
}: UserFilterProps) => {
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const selectedLabel = !selectedUserId
    ? placeholder
    : selectedProfile?.id === selectedUserId
      ? profileLabel(selectedProfile)
      : "Selected user";

  return (
    <div className="space-y-2">
      {showLabel && (
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Users className="h-4 w-4" />
          User
        </label>
      )}
      <UserSearchPicker
        value={selectedUserId}
        allLabel={placeholder}
        onChange={(profile) => {
          setSelectedProfile(profile);
          onUserChange(profile?.id ?? null);
        }}
      >
        <button
          type="button"
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </UserSearchPicker>
    </div>
  );
};
